"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Flame, Layers, RotateCw } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const applicationsData = [{
  id: 1,
  title: "Continuous Fluid Bed Granulation & Drying",
  category: "Moisture & Particle Growth",
  description: "Real-time monitoring of moisture content, granule growth, and drying endpoint in continuous and batch fluidised bed systems.",
  iconName: "Flame",
  pageUrl: "/DryingEndpointPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/fluid_bed_granulation_process.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/fluid_bed_granulation_process.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/fluid_bed_granulation_process.png-wvc-sizes",
  imageAlt: "Continuous fluid bed granulation and drying monitoring setup",
  bullet1: "In-line moisture quantification",
  bullet2: "Endpoint determination without sample pull",
  bullet3: "Interfacing with standard fluid bed ports"
}, {
  id: 2,
  title: "High-Shear Blending Verification",
  category: "Uniformity & Distribution",
  description: "Direct verification of blend homogeneity and active pharmaceutical ingredient (API) dispersion inside high-shear and tumble blenders.",
  iconName: "RotateCw",
  pageUrl: "/BlendUniformityPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/high_shear_blending_verification.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/high_shear_blending_verification.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/high_shear_blending_verification.png-wvc-sizes",
  imageAlt: "High-shear blending verification analytical spectrometer",
  bullet1: "Non-destructive blend uniformity assessment",
  bullet2: "Detection of over-blending and segregation",
  bullet3: "Wireless or through-window optical probes"
}, {
  id: 3,
  title: "Reaction Chemistry Endpoint Detection",
  category: "Synthesis & Kinetics",
  description: "In situ molecular tracking of reactant depletion, intermediate formation, and final product yield during organic synthesis.",
  iconName: "Activity",
  pageUrl: "/ProcessAnalyticalTechnologyPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/reaction_chemistry_monitoring.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/reaction_chemistry_monitoring.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/reaction_chemistry_monitoring.webp-wvc-sizes",
  imageAlt: "Chemical reactor vessel with in situ fiber optic analytical probe",
  bullet1: "Continuous kinetic tracking",
  bullet2: "Immediate identification of reaction completion",
  bullet3: "High-pressure and corrosive media compatibility"
}, {
  id: 4,
  title: "Extruder & Melt Flow Tracking",
  category: "Polymer & Hot-Melt Extrusion",
  description: "Continuous optical monitoring of polymer melts, solid dispersions, and compounding uniformity directly in the extruder barrel or die.",
  iconName: "Layers",
  pageUrl: "/PowderTestingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/extrusion_melt_flow_tracking.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/extrusion_melt_flow_tracking.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/extrusion_melt_flow_tracking.png-wvc-sizes",
  imageAlt: "Hot melt extrusion monitoring system in regulated production",
  bullet1: "Melt phase composition analysis",
  bullet2: "Solid dispersion state verification",
  bullet3: "High-temperature sensor probe integration"
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "Flame":
        return <Flame className="size-5 text-primary" aria-hidden="true" />;
      case "RotateCw":
        return <RotateCw className="size-5 text-primary" aria-hidden="true" />;
      case "Activity":
        return <Activity className="size-5 text-primary" aria-hidden="true" />;
      case "Layers":
        return <Layers className="size-5 text-primary" aria-hidden="true" />;
      default:
        return <Activity className="size-5 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="applications" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-3">
            Analytical Applications
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Process Analytical Technology for Regulated Manufacturing
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Standardised instrumentation configurations and optical interfaces
            engineered for monitoring critical quality attributes in pharmaceutical,
            biomedical, and fine chemical processing.
          </p>
        </div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {applicationsData.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.5,
          delay: shouldReduceMotion ? 0 : i * 0.1,
          ease: "easeOut"
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-shadow duration-200 overflow-hidden bg-card">
                <div>
                  {/* Visual Media */}
                  <div className="relative aspect-16/9 w-full bg-muted overflow-hidden border-b border-border">
                    <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs px-2.5 py-1 rounded-md text-xs font-medium text-foreground border border-border">
                      {item.category}
                    </div>
                  </div>

                  <CardHeader className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                        {getIcon(item.iconName)}
                      </div>
                      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        Application {i + 1}
                      </span>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <ul className="space-y-2.5 pt-2 border-t border-border">
                      <li className="flex items-start gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{item.bullet1}</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{item.bullet2}</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{item.bullet3}</span>
                      </li>
                    </ul>
                  </CardContent>
                </div>

                <div className="p-6 pt-4 border-t border-border/60 mt-auto bg-muted/20">
                  <Button asChild variant="outline" className="w-full justify-between">
                    <Link to={item.pageUrl}>
                      <span>View Application Details</span>
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Consultation Link Card */}
        <div className="mt-12 p-6 sm:p-8 rounded-xl bg-secondary border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-lg sm:text-xl font-semibold text-secondary-foreground font-default">
              Need evaluation for a custom process interface?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Technical feasibility testing and sample analysis are available to qualify optical measurement parameters prior to integration.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button asChild variant="default">
              <Link to="/FeasibilityStudiesPage">
                <span>Request Feasibility Study</span>
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/DiscussYourApplicationPage">
                <span>Discuss Your Application</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}